import User from "@models/user";
import axios, { AxiosError, AxiosInstance } from "axios";
import { authStore } from "../stores/main";
import { GameModel } from "@models/game";
import { getStoredCountry, getStoredCurrency } from "../utils/cookieUtils";
import { BillingAddressData } from "@models/billing-address";

export interface ILocation {
  countryCode: string;
  country: string;
  hosting: boolean;
  proxy: boolean;
  mobile: boolean;
}

export default class RendermixAPI {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_RENDERMIX_API_URL,
    });

    this.client.interceptors.request.use((config) => {
      // Add country and currency headers to all requests
      const country = getStoredCountry();
      const currency = getStoredCurrency();
      
      if (!country || !currency) {
        console.warn('Country or currency cookies not found in API request');
      }
      
      if (country) {
        config.headers.set('country', country);
      }
      if (currency) {
        config.headers.set('currency', currency);
      }

      const sessionToken = authStore.sessionToken;
      if (sessionToken) {
        config.headers.set("session_token", sessionToken);
      }

      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          authStore.logout();
        }

        return Promise.reject(error);
      }
    );
  }

  async getProfile() {
    const res = await this.client.get("/v1/accounts/profile");
    return new User(res.data);
  }

  async deleteSession(key: string) {
    await this.client.delete(`/v1/accounts/sessions/${key}`);
  }

  async getGameDetails(gameId: string) {
    const res = await this.client.get(`/v1/games/${gameId}/info`);
    return new GameModel(res.data);
  }

  /**
   * Submit billing address (POST).
   * Endpoint: POST /v1/accounts/billing-address
   * Body: { name, pin_code, street_address, city, country, state? }
   * Requires session_token header (set by interceptor).
   */
  async updateBillingAddress(data: BillingAddressData) {
    await this.client.post("/v1/accounts/billing-address", data);
  }

  async updateBillingAddressField(field: string, value: string) {
    await this.client.put("/v1/accounts/billing-address", { [field]: value });
  }

  async getCurrentLocation(): Promise<ILocation> {
    const res = await this.client.get("/v1/location");
    return res.data;
  }
}
